import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

// export interface ParagraphSkeletonProps {
//   sections?: number;
// }

// export function ParagraphSkeleton({ sections = 2 }: ParagraphSkeletonProps) {
//   return (
//     <Box border="2px solid red">
//       {Array.from({ length: sections }).map((_, i) => (
//         <Box key={i} sx={{ mb: 3 }}>
//           {/* Heading skeleton */}
//           <Skeleton width={`50%`} height={32} sx={{ mb: 2, maxWidth: '100%' }} />
//           {/* Paragraph lines */}
//           <Skeleton width="95%" sx={{ mb: 1 }} />
//           <Skeleton width="90%" sx={{ mb: 1 }} />
//           <Skeleton width="80%" />
//         </Box>
//       ))}
//     </Box>
//   );
// }

/**
 * Props for the ParagraphSkeleton component.
 */
export interface ParagraphSkeletonProps {
  /**
   * Total number of skeleton lines per paragraph.
   * @default 5
   */
  lines?: number;

  /**
   * Number of paragraphs to render.
   * Each paragraph will be separated by `paragraphGap`.
   * @default 1
   */
  paragraphs?: number;

  /**
   * Whether the last line of each paragraph should be shorter
   * (for a more natural text look).
   * @default true
   */
  lastLineShort?: boolean;

  /**
   * Animation style of the skeleton.
   * Can be "pulse", "wave", or false for no animation.
   * @default "wave"
   */
  animation?: 'pulse' | 'wave' | false;

  /**
   * Pixel height of each skeleton line.
   * Useful to match your typography line-height.
   * @default 24
   */
  lineHeight?: number;

  /**
   * Vertical gap (in pixels) between skeleton lines.
   * @default 8
   */
  gap?: number;

  /**
   * Vertical gap (in pixels) between paragraphs.
   * @default 24
   */
  paragraphGap?: number;

  /**
   * Optional array of width percentages (0–100) for each line.
   * If not provided, widths will be auto-generated to mimic text.
   * Example: [95, 92, 90, 88, 60]
   */
  widths?: number[];
}

/**
 * Renders a paragraph-like loading animation using MUI Skeleton lines.
 */
export function ParagraphSkeleton({
  lines = 5,
  paragraphs = 1,
  lastLineShort = true,
  animation = 'wave',
  lineHeight = 24,
  gap = 8,
  paragraphGap = 24,
  widths,
}: ParagraphSkeletonProps) {
  // Generate natural-looking widths if none provided
  const generateWidths = useCallback(
    (count: number) => {
      if (widths && widths.length) return widths.slice(0, count);

      const arr: number[] = [];
      for (let i = 0; i < count; i++) {
        const base =
          i === count - 1 && lastLineShort
            ? 55 + Math.random() * 15 // 55–70%
            : 85 + Math.random() * 12; // 85–97%
        arr.push(Math.round(base));
      }
      return arr;
    },
    [lastLineShort, widths],
  );

  return (
    <Box role="status" aria-label="Loading content">
      {Array.from({ length: paragraphs }).map((_, pIndex) => {
        const autoWidths = generateWidths(lines);

        return (
          <Box key={pIndex} sx={{ mb: pIndex === paragraphs - 1 ? 0 : `${paragraphGap}px` }}>
            {Array.from({ length: lines }).map((_, i) => (
              <Box key={i} sx={{ mb: i === lines - 1 ? 0 : `${gap}px` }}>
                <Skeleton
                  variant="text"
                  animation={animation}
                  sx={{
                    width: `${autoWidths[i]}%`,
                    height: `${lineHeight}px`,
                    borderRadius: '8px',
                  }}
                />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

export default ParagraphSkeleton;
